import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Reservations.css';
import '../../components/common/IconButton.css';
import { createFile, getFiles } from '../../services/fileService';
import Table from '../../components/common/Table';
import { formatDate } from '../../utils/date';
import CreateFileModal from '../../components/modals/CreateFileModal';
import FilesFiltersModal from '../../components/modals/FilesFiltersModal';
import { IconArrowRight, IconFilter } from '../../components/common/Icons';

const FilesPage = () => {
  const DEFAULT_STATUS_FILTERS = ['Completo', 'Incompleto'];
  const [files, setFiles] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [creationDateFrom, setCreationDateFrom] = useState('');
  const [creationDateTo, setCreationDateTo] = useState('');
  const [statusFilters, setStatusFilters] = useState(DEFAULT_STATUS_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createFileModalOpen, setCreateFileModalOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigate = useNavigate();

  const loadFiles = async () => {
    setLoading(true);
    try {
      const data = await getFiles();
      setFiles(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const filtered = useMemo(() => {
    const q = clientFilter.toLowerCase();
    return files.filter((f) => {
      const dateIn = (f.date_in ?? f.date_in)?.slice(0, 10) || '';
      const creationDate = f.creation_date?.slice(0, 10) || '';
      const status = f.status || 'Incompleto';
      if (dateFrom && (!dateIn || dateIn < dateFrom)) return false;
      if (dateTo && (!dateIn || dateIn > dateTo)) return false;
      if (creationDateFrom && (!creationDate || creationDate < creationDateFrom)) return false;
      if (creationDateTo && (!creationDate || creationDate > creationDateTo)) return false;
      if (!statusFilters.includes(status)) return false;

      const passengerNames = Array.isArray(f.passengers)
        ? f.passengers
            .map((passenger) => `${passenger.surname || passenger.last_name || ''} ${passenger.name || passenger.first_name || ''}`)
            .join(' ')
        : '';
      const searchableNames = [
        f.client_surname,
        f.client_name,
        f.passenger_names,
        f.passengerNames,
        passengerNames
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableNames.includes(q);
    });
  }, [files, clientFilter, dateFrom, dateTo, creationDateFrom, creationDateTo, statusFilters]);

  const columns = [
    {
      id: 'id',
      header: '#',
      accessor: (file) => file.id,
      filterPlaceholder: 'Filtrar #'
    },
    {
      id: 'date_in',
      header: 'Fecha de Salida',
      accessor: (file) => formatDate(file.date_in ?? file.dateIn),
      sortAccessor: (file) => (file.date_in ?? file.dateIn) || '',
      filterPlaceholder: 'YYYY-MM-DD'
    },
    {
      id: 'date_out',
      header: 'Fecha de Vuelta',
      accessor: (file) => formatDate(file.date_out ?? file.dateOut),
      sortAccessor: (file) => (file.date_out ?? file.dateOut) || '',
      filterPlaceholder: 'YYYY-MM-DD'
    },
    {
      id: 'client',
      header: 'Cliente',
      accessor: (file) => `${file.client_surname || ''}, ${file.client_name || ''}`.trim(),
      filterPlaceholder: 'Filtrar cliente'
    },
    {
      id: 'creation_date',
      header: 'Fecha de Creación',
      accessor: (file) => formatDate(file.creation_date),
      sortAccessor: (file) => file.creation_date || '',
      filterPlaceholder: 'YYYY-MM-DD'
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (file) => (
        <span
          className={`tag status-tag ${
            file.status === 'Completo'
              ? 'status-completo'
              : file.status === 'Deprecado'
                ? 'status-deprecado'
                : 'status-incompleto'
          }`}
        >
          {(file.status || 'Incompleto').toUpperCase()}
        </span>
      ),
      sortAccessor: (file) => file.status || 'Incompleto'
    },
    {
      id: 'actions',
      header: 'Acciones',
      headerClassName: 'th-center',
      sortable: false,
      filterable: false,
      cell: (file) => (
        <div className="cell-center">
          <button
            type="button"
            className="icon-btn icon-btn--select"
            onClick={() => navigate(`/backoffice/ficha/${file.id}`)}
            aria-label="Ver ficha"
            title="Ver ficha"
          >
            <IconArrowRight />
          </button>
        </div>
      )
    }
  ];

  const handleConfirmCreateFile = async ({ clientId, dateIn, dateOut }) => {
    try {
      setError('');
      const today = new Date().toISOString().split('T')[0];
      const file = await createFile({
        clientId,
        creationDate: today,
        dateIn,
        dateOut,
        status: 'Incompleto'
      });
      setCreateFileModalOpen(false);
      navigate(`/backoffice/ficha/${file.id}`);
    } catch (err) {
      console.error(err);
      setError('No se pudo crear la ficha');
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div className="actions-row">
          <h1>Fichas de Reserva</h1>
          <button
            type="button"
            className="icon-btn icon-btn--edit"
            onClick={() => setFiltersOpen(true)}
            aria-label="Filtrar fichas"
            title="Filtrar"
          >
            <IconFilter />
          </button>
        </div>
        <div className="actions-row">
          <input
            className="search-input"
            placeholder="Filtrar por cliente o pasajero"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          />
          <button className="btn" onClick={() => setCreateFileModalOpen(true)}>
            + Nueva Ficha
          </button>
        </div>
      </div>

      {error && <div className="form-error-message" role="alert">{error}</div>}
      {loading ? (
        <p>Cargando...</p>
      ) : (
        <Table
          data={filtered}
          columns={columns}
          rowKey="id"
          className="table"
          emptyMessage="No hay fichas para mostrar."
        />
      )}

      <CreateFileModal
        isOpen={createFileModalOpen}
        onClose={() => setCreateFileModalOpen(false)}
        onConfirm={handleConfirmCreateFile}
      />

      <FilesFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        initialDateFrom={dateFrom}
        initialDateTo={dateTo}
        initialCreationDateFrom={creationDateFrom}
        initialCreationDateTo={creationDateTo}
        initialStatuses={statusFilters}
        onApply={({ dateFrom: df, dateTo: dt, creationDateFrom: cdf, creationDateTo: cdt, statuses }) => {
          setDateFrom(df || '');
          setDateTo(dt || '');
          setCreationDateFrom(cdf || '');
          setCreationDateTo(cdt || '');
          setStatusFilters(Array.isArray(statuses) && statuses.length ? statuses : []);
          setFiltersOpen(false);
        }}
      />
    </div>
  );
};

export default FilesPage;
